(function () {
  if (window.TallahPremiumPackage) return;

  var PROPERTY = '_premium_package';
  var ORIGIN_PROPERTY = '_premium_origin';
  var MANUAL_TIER_PROPERTY = '_premium_manual_tier';
  var mutationQueue = Promise.resolve();
  var mutationLocked = false;
  var syncQueued = false;
  var syncTimer = null;
  var checkoutPreparing = false;
  var lastKnownCart = null;
  var CHECKOUT_SETTLE_DELAY = 650;
  var CHECKOUT_STABILITY_INTERVAL = 300;
  var CHECKOUT_STABILITY_ATTEMPTS = 5;

  function isArabic() {
    return document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl';
  }

  function parseNumber(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function cartRoute(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + String(path || '').replace(/^\//, '');
  }

  function cartRequest(path, options) {
    var url = cartRoute(path);
    if (window.TallahCart && typeof window.TallahCart.request === 'function') {
      return window.TallahCart.request(url, options || {});
    }
    return fetch(url, Object.assign({ credentials: 'same-origin' }, options || {})).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (payload) {
        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || 'Cart request failed');
        }
        return payload;
      });
    });
  }

  function fetchCart() {
    return cartRequest('cart.js', { cache: 'no-store' });
  }

  function configuredTierForItem(item) {
    if (!item) return '';
    var selector = document.querySelector('premium-package-selector');
    if (!selector) return '';
    var variantId = parseNumber(item.variant_id || item.id, null);
    if (variantId === parseNumber(selector.dataset.smallVariantId, null)) return 'small';
    if (variantId === parseNumber(selector.dataset.largeVariantId, null)) return 'large';
    return '';
  }

  function isTaggedPackageItem(item) {
    return !!(item && item.properties && item.properties[PROPERTY]);
  }

  function isPackageItem(item) {
    return isTaggedPackageItem(item) || !!configuredTierForItem(item);
  }

  function packageTier(item) {
    if (!isPackageItem(item)) return '';
    if (isTaggedPackageItem(item)) return String(item.properties[PROPERTY]).toLowerCase();
    return configuredTierForItem(item);
  }

  function packageOrigin(item) {
    if (!isPackageItem(item)) return '';
    if (!isTaggedPackageItem(item)) return 'manual';
    return String(item.properties[ORIGIN_PROPERTY] || 'earned').toLowerCase();
  }

  function packageManualTier(item) {
    if (!isPackageItem(item) || packageOrigin(item) !== 'manual') return '';
    return String((item.properties && item.properties[MANUAL_TIER_PROPERTY]) || packageTier(item)).toLowerCase();
  }

  function baseSubtotal(cart) {
    return (cart.items || []).reduce(function (total, item) {
      if (isPackageItem(item)) return total;
      if (typeof item.final_line_price === 'number') return total + item.final_line_price;
      return total + parseNumber(item.line_price, 0);
    }, 0);
  }

  function packageItems(cart) {
    return (cart.items || []).filter(isPackageItem);
  }

  function expectedPackagePrice(subtotal, tier, config, smallPrice, largePrice) {
    if (subtotal >= config.largeThreshold) return 0;
    if (subtotal >= config.smallThreshold) {
      return tier === 'large' ? Math.max(0, largePrice - smallPrice) : 0;
    }
    return tier === 'large' ? largePrice : smallPrice;
  }

  function normalizedProperties(properties) {
    var entries = Array.isArray(properties)
      ? properties.map(function (pair) { return [String(pair[0]), String(pair[1])]; })
      : Object.keys(properties || {}).map(function (key) { return [String(key), String(properties[key])]; });
    return JSON.stringify(entries.sort(function (a, b) { return a[0].localeCompare(b[0]); }));
  }

  function lineIdentity(item) {
    if (!item) return null;
    return {
      variant: String(item.variant_id || item.id || ''),
      properties: normalizedProperties(item.properties),
      plan: String((item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id) || '')
    };
  }

  function resolveLine(cart, key, identity) {
    var items = (cart && cart.items) || [];
    var exactIndex = items.findIndex(function (item) { return item.key === key; });
    if (exactIndex >= 0) return { item: items[exactIndex], line: exactIndex + 1 };
    if (!identity) return null;
    var candidates = items.map(function (item, index) {
      return { item: item, line: index + 1 };
    }).filter(function (entry) {
      var current = lineIdentity(entry.item);
      return current.variant === identity.variant &&
        current.properties === identity.properties &&
        current.plan === identity.plan;
    });
    if (candidates.length === 1) return candidates[0];

    // The premium package app can rewrite origin/tier properties while it is
    // reconciling the cart. The package variant is unique, so it remains a
    // safe identity when those properties changed between requests.
    var variantCandidates = items.map(function (item, index) {
      return { item: item, line: index + 1 };
    }).filter(function (entry) {
      return lineIdentity(entry.item).variant === identity.variant;
    });
    return variantCandidates.length === 1 ? variantCandidates[0] : null;
  }

  function requestLineChange(id, quantity) {
    return cartRequest('cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id, quantity: quantity })
    });
  }

  function changeLine(key, quantity, item) {
    var identity = lineIdentity(item);
    return requestLineChange(key, quantity).catch(function (error) {
      var message = String((error && error.message) || error || '');
      if (!/no valid id|line parameter/i.test(message)) throw error;
      // Cart apps can rewrite line keys between the cart refresh and this
      // request. Resolve the package again from the latest cart before retrying.
      return fetchCart().then(function (cart) {
        var resolved = resolveLine(cart, key, identity);
        if (!resolved) throw error;
        return requestLineChange(resolved.item.key || resolved.line, quantity);
      });
    });
  }

  function addLine(variantId, tier, origin, manualTier) {
    var properties = {
      _premium_package: tier,
      _premium_origin: origin,
      _premium_locked: '1'
    };
    if (origin === 'manual') properties[MANUAL_TIER_PROPERTY] = manualTier || tier;

    return cartRequest('cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: properties
      })
    });
  }

  function refreshCart(cart) {
    if (typeof window.forceRecalcCart === 'function') {
      return Promise.resolve(window.forceRecalcCart()).then(function () {
        return typeof window.refreshCartDrawer === 'function' ? window.refreshCartDrawer(null, 'premium-package') : null;
      });
    }
    if (typeof window.refreshCartDrawer === 'function') return window.refreshCartDrawer(cart, 'premium-package');

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cart } }));
    return Promise.resolve();
  }

  function selectorConfig() {
    var selector = document.querySelector('premium-package-selector');
    if (!selector) return null;
    return {
      selector: selector,
      smallThreshold: parseNumber(selector.dataset.smallThreshold, 150000),
      largeThreshold: parseNumber(selector.dataset.largeThreshold, 500000),
      smallVariantId: parseNumber(selector.dataset.smallVariantId, null),
      largeVariantId: parseNumber(selector.dataset.largeVariantId, null),
      smallAvailable: selector.dataset.smallAvailable === 'true',
      largeAvailable: selector.dataset.largeAvailable === 'true'
    };
  }

  function removeItems(items, keepKey) {
    var queue = Promise.resolve();
    items.forEach(function (item) {
      if (item.key === keepKey) return;
      queue = queue.then(function () { return changeLine(item.key, 0, item); });
    });
    return queue;
  }

  function keepOnly(items, item) {
    return removeItems(items, item ? item.key : null).then(function () {
      if (item && Number(item.quantity) !== 1) return changeLine(item.key, 1, item);
    });
  }

  function replacePackage(cart, tier, origin, config, manualTier) {
    var items = packageItems(cart);
    var targetVariantId = tier === 'large' ? config.largeVariantId : config.smallVariantId;
    var targetAvailable = tier === 'large' ? config.largeAvailable : config.smallAvailable;
    if (!targetVariantId) return Promise.reject(new Error('Package variant is not configured'));
    if (!targetAvailable) return Promise.reject(new Error('The selected TALLAH Box is currently unavailable'));
    var currentManualTier = manualTier || (items[0] ? packageManualTier(items[0]) : '') || (origin === 'manual' ? tier : '');

    return removeItems(items).then(function () {
      return addLine(targetVariantId, tier, origin, currentManualTier);
    });
  }

  function reconcile(cart, config) {
    var subtotal = baseSubtotal(cart);
    var items = packageItems(cart);
    var small = items.find(function (item) { return packageTier(item) === 'small'; });
    var large = items.find(function (item) { return packageTier(item) === 'large'; });

    if (subtotal >= config.largeThreshold) {
      if (large && isTaggedPackageItem(large)) return keepOnly(items, large);
      return replacePackage(cart, 'large', items[0] ? packageOrigin(items[0]) : 'earned', config);
    }

    if (subtotal >= config.smallThreshold) {
      // A Large box earned at the 5,000 threshold must step back to Small as
      // soon as the qualifying subtotal drops below that threshold. Only a
      // deliberately paid Large selection remains Large in this range.
      if (large && packageOrigin(large) === 'earned') return replacePackage(cart, 'small', 'earned', config);
      if (large && packageManualTier(large) === 'small') return replacePackage(cart, 'small', 'manual', config, 'small');
      if (large && isTaggedPackageItem(large)) return keepOnly(items, large);
      if (small && isTaggedPackageItem(small)) return keepOnly(items, small);
      if (large) return replacePackage(cart, 'large', packageOrigin(large), config);
      if (small) return replacePackage(cart, 'small', packageOrigin(small), config);
      return replacePackage(cart, 'small', 'earned', config);
    }

    var manual = items.find(function (item) { return packageOrigin(item) === 'manual'; });
    if (manual) {
      var manualTier = packageManualTier(manual) || packageTier(manual);
      if (packageTier(manual) !== manualTier || !isTaggedPackageItem(manual)) {
        return replacePackage(cart, manualTier, 'manual', config, manualTier);
      }
      return keepOnly(items, manual);
    }
    return removeItems(items);
  }

  function needsReconcile(cart, config) {
    var subtotal = baseSubtotal(cart);
    var items = packageItems(cart);
    var small = items.find(function (item) { return packageTier(item) === 'small'; });
    var large = items.find(function (item) { return packageTier(item) === 'large'; });
    var manual = items.find(function (item) { return packageOrigin(item) === 'manual'; });

    if (subtotal >= config.largeThreshold) {
      return items.length !== 1 || !large || !isTaggedPackageItem(large) || Number(large.quantity) !== 1;
    }
    if (subtotal >= config.smallThreshold) {
      var kept = large || small;
      var mustRestoreSmall = !!large && packageManualTier(large) === 'small';
      var mustDowngradeEarnedLarge = !!large && packageOrigin(large) === 'earned';
      return items.length !== 1 || !kept || mustRestoreSmall || mustDowngradeEarnedLarge || !isTaggedPackageItem(kept) || Number(kept.quantity) !== 1;
    }
    if (!manual) return items.length > 0;
    return items.length !== 1 || !isTaggedPackageItem(manual) || packageTier(manual) !== packageManualTier(manual) || Number(manual.quantity) !== 1;
  }

  function enqueueMutation(work) {
    var operation = mutationQueue.then(function () {
      var execute = function () {
      mutationLocked = true;
      window.__cartMutationLock = true;
      return work()
        .then(fetchCart)
        .then(function (cart) {
          updateAll(cart);
          return refreshCart(cart).then(function () { return cart; });
        })
        .finally(function () {
          mutationLocked = false;
          window.__cartMutationLock = false;
          if (syncQueued) {
            syncQueued = false;
            scheduleSync(80);
          }
        });
      };
      return window.TallahCart ? window.TallahCart.enqueue(execute) : execute();
    });
    mutationQueue = operation.catch(function (error) {
      mutationLocked = false;
      window.__cartMutationLock = false;
      console.error('Premium package update failed:', error);
      document.querySelectorAll('premium-package-selector').forEach(function (selector) {
        selector.showError(error.message);
      });
    });
    return operation;
  }

  function sync() {
    var config = selectorConfig();
    if (!config) return Promise.resolve();
    if (mutationLocked || window.__cartItemsUpdateInProgress || window.__cartMutationLock) {
      syncQueued = true;
      
      return Promise.resolve();
    }

    var execute = function () { return fetchCart().then(function (cart) {
      updateAll(cart);
      if (!needsReconcile(cart, config)) return;

      mutationLocked = true;
      window.__cartMutationLock = true;
      return reconcile(cart, config).then(function () {
        return fetchCart();
      }).then(function (afterCart) {
        updateAll(afterCart);
        return refreshCart(afterCart);
      }).finally(function () {
        mutationLocked = false;
        window.__cartMutationLock = false;
        if (syncQueued) {
          syncQueued = false;
          scheduleSync(80);
        }
      });
    }); };

    return (window.TallahCart ? window.TallahCart.enqueue(execute) : execute()).catch(function (error) {
      mutationLocked = false;
      window.__cartMutationLock = false;
      console.error('Premium package sync failed:', error);
    });
  }

  function updateAll(cart) {
    if (cart && Array.isArray(cart.items)) lastKnownCart = cart;
    document.querySelectorAll('premium-package-progress').forEach(function (progress) {
      progress.update(cart);
    });
    document.querySelectorAll('premium-package-selector').forEach(function (selector) {
      selector.update(cart);
    });
  }

  function scheduleSync(delay) {
    if (syncTimer) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(function () {
      syncTimer = null;
      sync();
    }, typeof delay === 'number' ? delay : 60);
  }

  function checkoutState(cart, config) {
    var subtotal = baseSubtotal(cart);
    var items = packageItems(cart);
    var item = items[0];

    if (items.length > 1) return { ready: false, message: 'More than one TALLAH Box is in the cart.' };
    if (subtotal < config.smallThreshold) {
      return { ready: !item || (isTaggedPackageItem(item) && Number(item.quantity) === 1) };
    }
    if (!item || !isTaggedPackageItem(item) || Number(item.quantity) !== 1) {
      return { ready: false, message: 'The complimentary TALLAH Box is still being prepared.' };
    }

    var tier = packageTier(item);
    if (subtotal >= config.largeThreshold && tier !== 'large') {
      return { ready: false, message: 'The Large TALLAH Box is still being prepared.' };
    }
    if (subtotal < config.largeThreshold && tier !== 'small' && tier !== 'large') {
      return { ready: false, message: 'The TALLAH Box selection is invalid.' };
    }

    var expectedPrice = expectedPackagePrice(
      subtotal,
      tier,
      config,
      parseNumber(config.selector.dataset.smallPrice, 0),
      parseNumber(config.selector.dataset.largePrice, 0)
    );
    var finalPrice = parseNumber(item.final_line_price, item.final_price || 0);
    if (finalPrice > expectedPrice) {
      return { ready: false, message: 'The TALLAH packaging discount is not applied yet.' };
    }
    return { ready: true };
  }

  function cartSignature(cart) {
    return (cart && Array.isArray(cart.items) ? cart.items : []).map(function (item) {
      return [
        item.key || item.id || item.variant_id,
        Number(item.quantity || 0),
        Number(item.final_line_price || 0)
      ].join(':');
    }).sort().join('|');
  }

  function waitForStableCart(previousSignature, attempt) {
    return fetchCart().then(function (cart) {
      var signature = cartSignature(cart);
      if (previousSignature && signature === previousSignature) return cart;
      if (attempt >= CHECKOUT_STABILITY_ATTEMPTS - 1) return cart;
      return new Promise(function (resolve) {
        window.setTimeout(resolve, CHECKOUT_STABILITY_INTERVAL);
      }).then(function () {
        return waitForStableCart(signature, attempt + 1);
      });
    });
  }

  function prepareCartFormForCheckout(form, cart) {
    form.querySelectorAll('input[name^="updates"]').forEach(function (input) {
      // Responsive cart markup can contain two quantity inputs for one line.
      // Checkout must use the verified server cart without resubmitting any
      // quantities; Shopify will otherwise recalculate split BOGO lines.
      input.disabled = true;
    });
    form.querySelectorAll('[data-premium-checkout-update]').forEach(function (input) {
      input.remove();
    });
  }

  function prepareCheckout() {
    var idle = window.TallahCart && typeof window.TallahCart.whenIdle === 'function'
      ? window.TallahCart.whenIdle()
      : Promise.resolve();

    return idle.then(sync).then(function () {
      // BOGO apps can finish splitting paid/free lines just after the visible
      // cart update. Require two identical server snapshots before checkout.
      return new Promise(function (resolve) {
        window.setTimeout(resolve, CHECKOUT_SETTLE_DELAY);
      });
    }).then(function () {
      return waitForStableCart('', 0);
    }).then(function (cart) {
      var config = selectorConfig();
      if (!config) throw new Error('Premium Package is enabled but its two products are not configured.');
      updateAll(cart);
      var state = checkoutState(cart, config);
      if (!state.ready) throw new Error(state.message || 'The TALLAH Box is not ready for checkout.');
      return cart;
    });
  }

  class PremiumPackageProgress extends HTMLElement {
    connectedCallback() {
      this.message = this.querySelector('[data-package-progress-message]');
      this.fill = this.querySelector('[data-package-progress-fill]');
      this.smallKnot = this.querySelector('[data-package-progress-knot="small"]');
      this.largeKnot = this.querySelector('[data-package-progress-knot="large"]');
    }

    update(cart) {
      if (!cart || Number(cart.item_count) === 0) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      if (!this.message || !this.fill) this.connectedCallback();
      var smallThreshold = parseNumber(this.dataset.smallThreshold, 150000);
      var largeThreshold = parseNumber(this.dataset.largeThreshold, 500000);
      var subtotal = baseSubtotal(cart);
      var reachedSmall = subtotal >= smallThreshold;
      var reachedLarge = subtotal >= largeThreshold;
      var languageArabic = isArabic();

      this.smallKnot.classList.toggle('is-reached', reachedSmall);
      this.largeKnot.classList.toggle('is-reached', reachedLarge);
      this.fill.style.width = Math.max(0, Math.min(100, (subtotal / largeThreshold) * 100)) + '%';

      if (reachedLarge) {
        this.message.textContent = languageArabic
          ? 'اكتمل الشريط. أكبر صندوق TALLAH في سلتك.'
          : 'Ribbon complete. Our largest TALLAH Box is in your cart.';
      } else if (reachedSmall) {
        this.message.textContent = languageArabic
          ? 'تم فتح الصندوق الصغير. باقي ' + this.money(largeThreshold - subtotal) + ' للصندوق الكبير.'
          : 'Small box unlocked. ' + this.money(largeThreshold - subtotal) + ' to the Large box.';
      } else {
        this.message.textContent = languageArabic
          ? 'باقي ' + this.money(smallThreshold - subtotal) + ' وصندوق TALLAH علينا.'
          : this.money(smallThreshold - subtotal) + ' away and the TALLAH Box is on us.';
      }
    }

    money(cents) {
      return isArabic() ? Math.ceil(cents / 100).toLocaleString('ar-EG') + ' ج.م' : 'LE ' + Math.ceil(cents / 100).toLocaleString('en-US');
    }
  }

  class PremiumPackageSelector extends HTMLElement {
    connectedCallback() {
      if (this._premiumPackageConnected) return;
      this._premiumPackageConnected = true;
      this.paidState = this.querySelector('[data-package-paid-state]');
      this.earnedState = this.querySelector('[data-package-earned-state]');
      this.optional = this.querySelector('[data-package-optional]');
      this.status = this.querySelector('[data-package-status]');
      this.currentImage = this.querySelector('[data-package-current-image]');
      this.currentTitle = this.querySelector('[data-package-current-title]');
      this.currentBadge = this.querySelector('[data-package-current-badge]');
      this.currentDescription = this.querySelector('[data-package-current-description]');
      this.currentPrice = this.querySelector('[data-package-current-price]');
      this.currentOriginal = this.querySelector('[data-package-current-original]');
      this.currentFinal = this.querySelector('[data-package-current-final]');
      this.upgrade = this.querySelector('[data-package-upgrade]');
      this.upgradeDone = this.querySelector('[data-package-upgrade-done]');
      this.querySelectorAll('[data-package-select]').forEach(function (button) {
        button.addEventListener('click', this.onSelect.bind(this));
      }, this);
      if (this.upgrade) this.upgrade.addEventListener('click', this.onUpgrade.bind(this));
      var undo = this.querySelector('[data-package-undo]');
      if (undo) undo.addEventListener('click', this.onUndo.bind(this));
      if (lastKnownCart) this.update(lastKnownCart);
      else scheduleSync(0);
    }

    config() {
      return {
        smallThreshold: parseNumber(this.dataset.smallThreshold, 150000),
        largeThreshold: parseNumber(this.dataset.largeThreshold, 500000),
        smallVariantId: parseNumber(this.dataset.smallVariantId, null),
        largeVariantId: parseNumber(this.dataset.largeVariantId, null),
        smallAvailable: this.dataset.smallAvailable === 'true',
        largeAvailable: this.dataset.largeAvailable === 'true'
      };
    }

    onSelect(event) {
      var button = event.currentTarget;
      var tier = button.dataset.packageSelect;
      var config = this.config();
      var removing = button.getAttribute('aria-pressed') === 'true';
      var message = removing
        ? (isArabic() ? 'جارٍ إزالة صندوق TALLAH…' : 'Removing TALLAH Box…')
        : (isArabic() ? 'جارٍ إضافة صندوق TALLAH…' : 'Adding TALLAH Box…');
      if (!this.startMutation(button, message, !removing)) return;
      enqueueMutation(function () {
        return fetchCart().then(function (cart) {
          var selected = packageItems(cart).find(function (item) { return packageTier(item) === tier; });
          if (selected && packageOrigin(selected) === 'manual') return removeItems(packageItems(cart));
          return replacePackage(cart, tier, 'manual', config, tier);
        });
      }).then(this.finishMutation.bind(this, true), this.finishMutation.bind(this, false));
    }

    onUpgrade(event) {
      var config = this.config();
      var message = isArabic() ? 'جارٍ ترقية صندوق TALLAH…' : 'Upgrading TALLAH Box…';
      if (!this.startMutation(event.currentTarget, message)) return;
      enqueueMutation(function () {
        return fetchCart().then(function (cart) {
          var current = packageItems(cart)[0];
          var origin = current ? packageOrigin(current) : 'earned';
          var manualTier = origin === 'manual' ? 'large' : '';
          return replacePackage(cart, 'large', origin, config, manualTier);
        });
      }).then(this.finishMutation.bind(this, true), this.finishMutation.bind(this, false));
    }

    onUndo(event) {
      var config = this.config();
      var message = isArabic() ? 'جارٍ استعادة الصندوق الصغير…' : 'Restoring the Small box…';
      if (!this.startMutation(event.currentTarget, message)) return;
      enqueueMutation(function () {
        return fetchCart().then(function (cart) {
          var current = packageItems(cart)[0];
          return replacePackage(cart, 'small', current ? packageOrigin(current) : 'earned', config);
        });
      }).then(this.finishMutation.bind(this, true), this.finishMutation.bind(this, false));
    }

    startMutation(button, message, optimisticSelected) {
      if (this._packageMutationPending) return false;
      this._packageMutationPending = true;
      this._pendingOptionState = [];
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');

      this.querySelectorAll('[data-package-select]').forEach(function (option) {
        this._pendingOptionState.push({
          button: option,
          selected: option.classList.contains('is-selected'),
          pressed: option.getAttribute('aria-pressed') || 'false'
        });
        option.disabled = true;
        if (typeof optimisticSelected === 'boolean') {
          var selected = option === button && optimisticSelected;
          option.classList.toggle('is-selected', selected);
          option.setAttribute('aria-pressed', selected ? 'true' : 'false');
        }
      }, this);

      this.querySelectorAll('[data-package-upgrade], [data-package-undo]').forEach(function (control) {
        control.disabled = true;
      });
      if (button) button.classList.add('is-pending');
      if (this.status) {
        this.status.dataset.packageLoading = 'true';
        this.status.textContent = message;
      }
      this.setCheckoutBlocked(true, message);
      return true;
    }

    finishMutation(succeeded) {
      if (!this._packageMutationPending) return;
      this._packageMutationPending = false;
      this.classList.remove('is-loading');
      this.removeAttribute('aria-busy');

      if (!succeeded && this._pendingOptionState) {
        this._pendingOptionState.forEach(function (state) {
          state.button.classList.toggle('is-selected', state.selected);
          state.button.setAttribute('aria-pressed', state.pressed);
        });
      }

      this.querySelectorAll('[data-package-select]').forEach(function (option) {
        var available = option.dataset.packageSelect === 'large'
          ? this.dataset.largeAvailable === 'true'
          : this.dataset.smallAvailable === 'true';
        option.disabled = !available;
        option.classList.remove('is-pending');
      }, this);
      this.querySelectorAll('[data-package-upgrade], [data-package-undo]').forEach(function (control) {
        control.disabled = false;
        control.classList.remove('is-pending');
      });

      if (this.status && this.status.dataset.packageLoading === 'true') {
        this.status.textContent = '';
        delete this.status.dataset.packageLoading;
      }
      this._pendingOptionState = null;
    }

    update(cart) {
      if (!this.paidState) this.connectedCallback();
      var config = this.config();
      var subtotal = baseSubtotal(cart);
      var item = packageItems(cart)[0];
      var tier = item ? packageTier(item) : '';
      var earned = subtotal >= config.smallThreshold;
      var upgraded = earned && tier === 'large' && subtotal < config.largeThreshold;
      var languageArabic = isArabic();

      this.paidState.hidden = earned;
      this.earnedState.hidden = !earned;
      this.optional.hidden = earned;

      this.querySelectorAll('[data-package-select]').forEach(function (button) {
        var selected = !!item && packageOrigin(item) === 'manual' && button.dataset.packageSelect === tier;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      if (!earned) {
        this.setCheckoutBlocked(false);
        this.status.textContent = '';
        return;
      }

      if (!item) {
        this.setCheckoutBlocked(true, languageArabic ? 'جاري تجهيز صندوق TALLAH المجاني.' : 'Preparing your complimentary TALLAH Box.');
        this.status.textContent = languageArabic ? 'جاري تجهيز صندوق TALLAH المجاني.' : 'Preparing your complimentary TALLAH Box.';
        return;
      }

      var isLarge = tier === 'large';
      var displayName = isLarge ? this.dataset.largeName : this.dataset.smallName;
      var displayImage = isLarge ? this.dataset.largeImage : this.dataset.smallImage;
      var finalPrice = parseNumber(item.final_line_price, item.final_price || 0);
      var originalPrice = parseNumber(item.original_line_price, item.original_price || 0);
      var expectedPrice = expectedPackagePrice(
        subtotal,
        tier,
        config,
        parseNumber(this.dataset.smallPrice, 0),
        parseNumber(this.dataset.largePrice, 0)
      );

      this.currentImage.src = displayImage || '';
      this.currentImage.alt = 'TALLAH Box - ' + displayName;
      this.currentTitle.textContent = 'TALLAH Box - ' + displayName;
      this.currentBadge.textContent = upgraded ? (languageArabic ? 'تمت الترقية' : 'Upgraded') : (languageArabic ? 'هدية' : 'Gifted');
      this.currentDescription.textContent = isLarge
        ? (languageArabic ? 'أكبر صندوق لدينا، مغلف يدويًا' : 'Our largest box, wrapped by hand')
        : (languageArabic ? 'تغليف يدوي، شريط ساتان وكارت باسمك' : 'Wrapped by hand, satin ribbon, name card');
      this.currentOriginal.textContent = originalPrice > finalPrice ? this.money(originalPrice) : '';
      this.currentOriginal.hidden = originalPrice <= finalPrice;
      this.currentFinal.textContent = finalPrice === 0 ? (languageArabic ? 'مجاني' : 'Free') : this.money(finalPrice);
      this.currentPrice.classList.toggle('is-free', finalPrice === 0);

      this.upgrade.hidden = !(subtotal >= config.smallThreshold && subtotal < config.largeThreshold && tier === 'small');
      this.upgradeDone.hidden = !upgraded;
      if (!this.upgrade.hidden) {
        this.querySelector('[data-package-upgrade-image]').src = this.dataset.largeImage || '';
        this.querySelector('[data-package-upgrade-copy]').innerHTML = languageArabic
          ? 'حوّليه إلى <strong>Large</strong> مقابل ' + this.money(Math.max(0, parseNumber(this.dataset.largePrice, 0) - parseNumber(this.dataset.smallPrice, 0)))
          : 'Make it the <strong>Large</strong> for ' + this.money(Math.max(0, parseNumber(this.dataset.largePrice, 0) - parseNumber(this.dataset.smallPrice, 0)));
      }
      if (!this.upgradeDone.hidden) {
        this.querySelector('[data-package-upgrade-done-copy]').innerHTML = languageArabic
          ? 'تمت الترقية إلى <strong>Large</strong>'
          : 'Upgraded to the <strong>Large</strong>';
      }

      var discountCorrect = finalPrice <= expectedPrice;
      this.classList.toggle('has-discount-warning', !discountCorrect);
      this.setCheckoutBlocked(
        !discountCorrect,
        languageArabic ? 'راجعي خصم تغليف TALLAH قبل الدفع.' : 'Review the TALLAH packaging discount before checkout.'
      );
      this.status.textContent = discountCorrect
        ? ''
        : (languageArabic ? 'خصم التغليف غير مطبق على سعر السلة بعد.' : 'The packaging discount is not applied to the cart price yet.');
    }

    money(cents) {
      if (window.FoxTheme && FoxTheme.Currency && typeof FoxTheme.Currency.formatMoney === 'function') {
        return FoxTheme.Currency.formatMoney(cents, this.dataset.moneyFormat || undefined);
      }
      return (isArabic() ? 'ج.م ' : 'LE ') + (cents / 100).toFixed(2);
    }

    showError(message) {
      this.setCheckoutBlocked(true, isArabic() ? 'تعذر تحديث صندوق TALLAH.' : 'The TALLAH Box could not be updated.');
      if (this.status) {
        delete this.status.dataset.packageLoading;
        this.status.textContent = isArabic() ? 'تعذر تحديث صندوق TALLAH. حاولي مرة أخرى.' : message;
      }
    }

    setCheckoutBlocked(blocked, message) {
      var drawer = this.closest('cart-drawer') || document;
      drawer.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach(function (button) {
        button.disabled = !!blocked;
        button.setAttribute('aria-disabled', blocked ? 'true' : 'false');
        if (blocked && message) button.setAttribute('title', message);
        else button.removeAttribute('title');
      });
    }
  }

  window.TallahPremiumPackage = {
    sync: sync,
    baseSubtotal: baseSubtotal,
    packageItems: packageItems,
    packageTier: packageTier,
    packageOrigin: packageOrigin,
    packageManualTier: packageManualTier,
    isPackageItem: isPackageItem,
    needsReconcile: needsReconcile,
    expectedPackagePrice: expectedPackagePrice,
    reconcile: reconcile,
    checkoutState: checkoutState,
    prepareCheckout: prepareCheckout,
    scheduleSync: scheduleSync
  };

  if (!customElements.get('premium-package-progress')) customElements.define('premium-package-progress', PremiumPackageProgress);
  if (!customElements.get('premium-package-selector')) customElements.define('premium-package-selector', PremiumPackageSelector);

  document.addEventListener('cart:updated', function (event) {
    if (event.detail && event.detail.cart) updateAll(event.detail.cart);
    // Premium Package mutations already end with a fresh, reconciled cart.
    // Avoid immediately requesting /cart.js again after their drawer refresh.
    if (!event.detail || event.detail.source !== 'premium-package') scheduleSync(60);
  });
  document.addEventListener('DOMContentLoaded', function () { scheduleSync(80); });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.dataset.premiumPackageCheckout !== 'true') return;
    var button = event.submitter || form.querySelector('button[name="checkout"]');
    if (!button || button.name !== 'checkout') return;
    if (form.dataset.premiumPackageVerified === 'true') {
      delete form.dataset.premiumPackageVerified;
      return;
    }

    event.preventDefault();
    if (checkoutPreparing) return;
    checkoutPreparing = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    prepareCheckout().then(function (cart) {
      // Keep delivery/cart attributes in the normal form, but omit every
      // quantity field so Shopify checks out the verified server cart as-is.
      prepareCartFormForCheckout(form, cart);
      form.dataset.premiumPackageVerified = 'true';
      button.disabled = false;
      button.removeAttribute('aria-busy');
      form.requestSubmit(button);
    }).catch(function (error) {
      var selector = document.querySelector('premium-package-selector');
      if (selector) {
        selector.showError(error.message);
        selector.setCheckoutBlocked(false);
      }
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }).finally(function () {
      checkoutPreparing = false;
    });
  }, true);
})();
